import { toPng } from 'html-to-image';

function safeFilePart(value) {
  return String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .slice(0, 80) || 'dororaid';
}

function captureDimensions(element) {
  const bounds = element.getBoundingClientRect();
  return {
    width: Math.max(1, Math.ceil(element.scrollWidth || bounds.width || 1)),
    height: Math.max(1, Math.ceil(element.scrollHeight || bounds.height || 1)),
  };
}

function capturePixelRatio(element, safeMode = false) {
  const { width, height } = captureDimensions(element);
  const pixelBudget = 22_000_000;
  const dimensionBudget = 8192;
  const deviceRatio = Math.max(1, Number(window.devicePixelRatio || 1));
  const requestedRatio = Math.min(safeMode ? 1 : 2, deviceRatio);
  const ratioByPixels = Math.sqrt(pixelBudget / Math.max(1, width * height));
  const ratioByDimension = Math.min(dimensionBudget / width, dimensionBudget / height);
  return Math.max(0.35, Math.min(requestedRatio, ratioByPixels, ratioByDimension));
}

function captureOptions(element, safeMode = false) {
  const { width, height } = captureDimensions(element);
  const pixelRatio = capturePixelRatio(element, safeMode);
  return {
    width,
    height,
    canvasWidth: Math.max(1, Math.floor(width * pixelRatio)),
    canvasHeight: Math.max(1, Math.floor(height * pixelRatio)),
    pixelRatio: 1,
    cacheBust: false,
    includeQueryParams: true,
    skipFonts: safeMode,
    backgroundColor: '#0b0e13',
    filter: (node) => !(node instanceof HTMLElement && node.dataset.captureExclude === 'true'),
  };
}

function delay(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function captureTimeout(milliseconds, stage) {
  return new Promise((_, reject) => {
    window.setTimeout(() => {
      const error = new Error(`capture_timeout:${stage}`);
      error.code = 'capture_timeout';
      reject(error);
    }, milliseconds);
  });
}

async function withCaptureTimeout(promise, milliseconds, stage) {
  return Promise.race([promise, captureTimeout(milliseconds, stage)]);
}

function imageSource(image) {
  return String(image.currentSrc || image.src || '').trim();
}

async function waitForCaptureAssets(element) {
  const images = [...element.querySelectorAll('img')]
    .filter((image) => image.dataset.captureExclude !== 'true' && imageSource(image));
  const originalLoading = images.map((image) => image.getAttribute('loading'));

  try {
    for (const image of images) image.loading = 'eager';

    const waits = [];
    if (document.fonts?.ready) waits.push(Promise.race([document.fonts.ready, delay(2000)]));
    for (const image of images) {
      if (image.complete && image.naturalWidth > 0) continue;
      waits.push(Promise.race([
        new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }),
        delay(2500),
      ]));
    }
    await Promise.allSettled(waits);

    const failed = images.filter((image) => imageSource(image) && (!image.complete || image.naturalWidth <= 0));
    if (failed.length) {
      const error = new Error(`capture_asset_failed:${failed.length}`);
      error.code = 'capture_asset_failed';
      throw error;
    }
  } finally {
    images.forEach((image, index) => {
      const loading = originalLoading[index];
      if (loading === null) image.removeAttribute('loading');
      else image.setAttribute('loading', loading);
    });
  }
}

function downloadDataUrl(dataUrl, filename) {
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => anchor.remove(), 1000);
}

function classifyCaptureError(error) {
  const detail = String(error?.message || error || 'capture_failed');
  const normalized = detail.toLowerCase();
  let code = String(error?.code || 'capture_failed');
  if (/capture_timeout|timeout/.test(normalized)) code = 'capture_timeout';
  else if (/fetch|cors|image|resource|network|asset/.test(normalized)) code = 'capture_asset_failed';
  else if (/canvas|memory|dimension|size|allocation|too large/.test(normalized)) code = 'capture_size_failed';
  const wrapped = new Error(code);
  wrapped.code = code;
  wrapped.detail = detail;
  wrapped.cause = error;
  return wrapped;
}

async function renderCaptureDataUrl(element) {
  let firstError = null;
  try {
    const dataUrl = await withCaptureTimeout(
      toPng(element, captureOptions(element, false)),
      22_000,
      'primary',
    );
    if (dataUrl) return dataUrl;
    firstError = new Error('capture_data_url_empty');
  } catch (error) {
    firstError = error;
  }

  try {
    const safeDataUrl = await withCaptureTimeout(
      toPng(element, captureOptions(element, true)),
      16_000,
      'safe',
    );
    if (safeDataUrl) return safeDataUrl;
    throw new Error('capture_safe_data_url_empty');
  } catch (safeError) {
    const combined = new Error(`${String(firstError?.message || firstError || '')} | ${String(safeError?.message || safeError || '')}`);
    throw classifyCaptureError(combined);
  }
}

export async function captureElementAsPng(element, filename) {
  if (!(element instanceof HTMLElement)) throw new Error('capture_target_missing');
  const safeFilename = `${safeFilePart(filename)}.png`;

  try {
    await withCaptureTimeout(waitForCaptureAssets(element), 5000, 'assets');
    const dataUrl = await renderCaptureDataUrl(element);
    downloadDataUrl(dataUrl, safeFilename);
    return { mode: 'download', filename: safeFilename };
  } catch (error) {
    throw classifyCaptureError(error);
  }
}
